import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid } from '@mui/material';
import { Celebration, CalendarMonth, ArrowForward } from '@mui/icons-material';
import './SelectEDM.css';
import visibenLogo from './VISIBEN.svg';
import festivalEDM from './Frame 6.svg';  
import specialDayEDM from './Frame 7.svg';

const edmOptions = [
  {
    img: festivalEDM,
    title: "Festival EDM's",
    alt: "Festival EDM's",
    description: "Create stunning festival greetings for Diwali, Holi, Eid & more",
    icon: Celebration,
    color: '#f59e0b'
  },
  {
    img: specialDayEDM,
    title: "Special Day EDM's",
    alt: "Special Day EDM's",
    description: "Celebrate special occasions like Mother's Day, Father's Day & more",
    icon: CalendarMonth,
    color: '#8b5cf6'
  },
];

const SelectEDM = () => {
  const [selected, setSelected] = useState(null);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const navigate = useNavigate();

  const handleCardClick = (idx) => {
    setSelected(idx);
    setTimeout(() => {
      const edmRoutes = ['../festival-edms-selection', '../special-days-edms'];
      navigate(edmRoutes[idx] || '../festival-edms-selection');
    }, 200);
  };

  return (
    <Box sx={{ p: 4, background: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{
        background: '#0b91d6',
        borderRadius: '16px',
        padding: '32px 40px',
        marginBottom: '32px',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-5%',
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%'
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            width: 56, height: 56, borderRadius: '12px', 
            bgcolor: 'rgba(255,255,255,0.2)', 
            display: 'flex', alignItems: 'center', justifyContent: 'center' 
          }}>
            <img src={visibenLogo} alt="VISIBEEN Logo" style={{ height: 32 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: 'white', fontFamily: 'Inter, sans-serif' }}>
              EDM Creator
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', fontFamily: 'Inter, sans-serif' }}>
              Create professional marketing designs for your business
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Selection Section */}
      <Paper sx={{ 
        p: 5, 
        borderRadius: '16px', 
        border: '1px solid #e2e8f0',
        boxShadow: 'none',
        background: 'white',
        textAlign: 'center'
      }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#1e293b', mb: 1, fontFamily: 'Inter, sans-serif' }}>
          Select Your EDM Type
        </Typography>
        <Typography sx={{ fontSize: '15px', color: '#64748b', mb: 5, maxWidth: 500, mx: 'auto' }}>
          Choose a template category to quickly create eye-catching posts for your business
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {edmOptions.map((edm, idx) => {
            const IconComponent = edm.icon;
            const isSelected = selected === idx;
            const isHovered = hoveredIdx === idx;
            
            return (
              <Grid item xs={12} sm={6} md={5} key={edm.title}>
                <Paper
                  onClick={() => handleCardClick(idx)}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  sx={{
                    p: 4,
                    borderRadius: '16px',
                    border: `2px solid ${isSelected ? edm.color : isHovered ? edm.color : '#e2e8f0'}`,
                    boxShadow: isSelected || isHovered ? `0 8px 24px ${edm.color}20` : '0 1px 3px rgba(0,0,0,0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    transform: isHovered ? 'translateY(-4px)' : 'none',
                    background: isSelected ? `${edm.color}08` : 'white',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Icon Badge */}
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '16px',
                    bgcolor: `${edm.color}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 3
                  }}>
                    <IconComponent sx={{ fontSize: 32, color: edm.color }} />
                  </Box>

                  {/* Image */}
                  <Box sx={{ mb: 3 }}>
                    <img 
                      src={edm.img} 
                      alt={edm.alt} 
                      style={{ 
                        width: 120, 
                        height: 120, 
                        objectFit: 'contain',
                        transition: 'transform 0.2s ease',
                        transform: isHovered ? 'scale(1.05)' : 'scale(1)'
                      }} 
                    />
                  </Box>

                  {/* Title */}
                  <Typography sx={{ 
                    fontSize: '18px', 
                    fontWeight: 600, 
                    color: '#1e293b', 
                    mb: 1,
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    {edm.title}
                  </Typography>

                  {/* Description */}
                  <Typography sx={{ 
                    fontSize: '14px', 
                    color: '#64748b', 
                    mb: 3,
                    lineHeight: 1.6
                  }}>
                    {edm.description}
                  </Typography>

                  {/* Action Indicator */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    color: edm.color,
                    fontWeight: 600,
                    fontSize: '14px',
                    opacity: isHovered ? 1 : 0.7,
                    transition: 'opacity 0.2s ease'
                  }}>
                    <span>Get Started</span>
                    <ArrowForward sx={{ fontSize: 18 }} />
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Box>
  );
};

export default SelectEDM; 